import { z } from 'zod';
import { DB_TYPE } from '@/common/define/common.define';
import type {
  BaseParameterDetailAddition,
  BaseSessionDetailAddition,
  BaseSqlDetailAddition,
} from '../types';
import { nullishAsUndefined, nullishToEmpty } from './utils';

export const sessionDetailSchema = z
  .object({
    instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
    sid: nullishAsUndefined(z.coerce.number().optional()),
    sessionId: nullishAsUndefined(z.coerce.number().optional()),
    stmtId: nullishAsUndefined(z.coerce.number().optional()),
    currentStmtId: nullishAsUndefined(z.coerce.number().optional()),
    loginTime: nullishAsUndefined(z.string().optional()),
    clientPid: nullishAsUndefined(z.coerce.number().optional()),
    fromTime: nullishAsUndefined(z.string().optional()),
    toTime: nullishAsUndefined(z.string().optional()),
    isOpenedFromPA: nullishAsUndefined(z.boolean().optional()),
    init: nullishAsUndefined(z.boolean().optional()),
  })
  .transform(({ sid, sessionId, stmtId, currentStmtId, ...rest }) => ({
    ...rest,
    sid: sid ?? sessionId,
    stmtId: stmtId ?? currentStmtId,
  }))
  .pipe(
    z
      .object({
        sid: z.number({ required_error: 'SID' }),
        stmtId: z.number({ required_error: 'Statement ID' }),
        loginTime: nullishAsUndefined(z.string().optional()),
        clientPid: nullishAsUndefined(z.number().optional()),
        instanceId: z.string().min(1, 'Instance ID'),
        fromTime: nullishAsUndefined(z.string().optional()),
        toTime: nullishAsUndefined(z.string().optional()),
        isOpenedFromPA: nullishAsUndefined(z.boolean().optional()),
        init: nullishAsUndefined(z.boolean().optional()),
      })
      .refine(({ loginTime, clientPid }) => loginTime != null || clientPid != null, {
        message: 'Login Time, Client PID',
        path: ['loginTime'],
      }),
  );

export const sqlDetailSchema = z.object({
  sqlId: z.string({ required_error: 'SQL ID' }).min(1, 'SQL ID'),
  instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
  fromTime: nullishAsUndefined(z.string().optional()),
  toTime: nullishAsUndefined(z.string().optional()),
  sqlTextHashId: nullishAsUndefined(z.string().optional()),
  stmtId: nullishAsUndefined(z.coerce.number().optional()),
  module: nullishAsUndefined(z.string().optional()),
  userName: nullishAsUndefined(z.string().optional()),
  lastQueryStartTime: nullishAsUndefined(z.string().optional()),
});

export const parameterDetailSchema = z
  .object({
    instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
    name: nullishAsUndefined(z.string().optional()),
    parameterName: nullishAsUndefined(z.string().optional()),
    fromTime: nullishAsUndefined(z.string().optional()),
    toTime: nullishAsUndefined(z.string().optional()),
  })
  .transform(({ name, parameterName, ...rest }) => ({
    ...rest,
    parameterName: name ?? parameterName,
  }))
  .pipe(
    z.object({
      parameterName: z.string({ required_error: 'Parameter Name' }).min(1, 'Parameter Name'),
      instanceId: z.string().min(1, 'Instance ID'),
      fromTime: z.string().optional(),
      toTime: z.string().optional(),
    }),
  );

export const replicationDetailSchema = z
  .object({
    instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
    id: nullishAsUndefined(z.string().optional()),
    replicationName: nullishAsUndefined(z.string().optional()),
    targetName: nullishAsUndefined(z.string().optional()),
    fromTime: nullishAsUndefined(z.string().optional()),
    toTime: nullishAsUndefined(z.string().optional()),
  })
  .transform(({ id, replicationName, targetName, ...rest }) => ({
    ...rest,
    replicationName: id ?? replicationName ?? targetName,
  }))
  .pipe(
    z.object({
      replicationName: z.string({ required_error: 'Replication Name' }).min(1, 'Replication Name'),
      instanceId: z.string().min(1, 'Instance ID'),
      fromTime: z.string().optional(),
      toTime: z.string().optional(),
    }),
  );

export const replicationObjectDetailSchema = z
  .object({
    instanceId: z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
    id: nullishAsUndefined(z.string().optional()),
    replicationName: nullishAsUndefined(z.string().optional()),
    objectName: z.string({ required_error: 'Object Name' }).min(1, 'Object Name'),
    fromTime: z.string({ required_error: 'From Time' }).min(1, 'From Time'),
    toTime: z.string({ required_error: 'To Time' }).min(1, 'To Time'),
    seriesColor: nullishAsUndefined(z.record(z.string()).optional()),
  })
  .transform(({ id, replicationName, ...rest }) => ({
    ...rest,
    replicationName: id ?? replicationName,
  }));

export const documentTextSchema = z.object({
  instanceId: z.preprocess(
    nullishToEmpty(''),
    z.string({ required_error: 'Instance ID' }).min(1, 'Instance ID'),
  ),
  sqlId: z.preprocess(nullishToEmpty(''), z.string({ required_error: 'SQL ID' }).min(1, 'SQL ID')),
  sqlTextHashId: nullishAsUndefined(z.string().optional()),
});

export type SessionDetailSchema = z.infer<typeof sessionDetailSchema>;
export type SqlDetailSchema = z.infer<typeof sqlDetailSchema>;
export type ParameterDetailSchema = z.infer<typeof parameterDetailSchema>;
export type ReplicationDetailSchema = z.infer<typeof replicationDetailSchema>;
export type ReplicationObjectDetailSchema = z.infer<typeof replicationObjectDetailSchema>;
export type DocumentTextSchema = z.infer<typeof documentTextSchema>;

export type AltibaseSessionDetailAddition = BaseSessionDetailAddition & SessionDetailSchema;
export type AltibaseSqlDetailAddition = BaseSqlDetailAddition & SqlDetailSchema;
export type AltibaseParameterDetailAddition = Omit<
  BaseParameterDetailAddition & ParameterDetailSchema,
  'parameterName'
>;
export type AltibaseReplicationDetailAddition = ReplicationDetailSchema & {
  dbType?: typeof DB_TYPE.ALTIBASE;
};
export type AltibaseReplicationObjectDetailAddition = Omit<
  ReplicationObjectDetailSchema,
  'replicationName'
> & { dbType?: typeof DB_TYPE.ALTIBASE };
